<section class="page-shell">
  <header class="page-hero dbw-card dbw-card--xl">
    <div>
      <p class="eyebrow">Contrôle de gestion</p>
      <h3>Coûts ex-post</h3>
      <p class="hero-subtitle">
        Importez le fichier « Cout ExPost » de l'année, générez les bordereaux réglementaires
        (mail et courrier), puis pilotez l'envoi des emails par lots.
      </p>
    </div>

    <oui-button
      type="outlined"
      iconSuffix="sync"
      [disabled]="loading()"
      (click)="reload()">
      Actualiser
    </oui-button>
  </header>

  <section class="content-grid">
    @if (canImport()) {
      <article class="panel dbw-card dbw-card--xl">
        <div class="panel-heading">
          <h4>Importer une campagne</h4>
        </div>

        <div class="form-controls">
          <div class="file-uploader-container">
            <oui-file-uploader
              type="single"
              accept=".xlsx"
              [value]="selectedFile() ? [selectedFile()!] : null"
              (valueChange)="onFilesChange($event)"
              [maxFileSize]="100"
              hint="Classeur Excel « Cout ExPost YYYY » avec les onglets ENVOYE et NON ENVOYE / Taille max : 100 Mo">
            </oui-file-uploader>

            @if (selectedFile(); as file) {
              <oui-file-item
                [file]="file"
                (delete)="removeFile()"></oui-file-item>
            }
          </div>

          <oui-textfield
            label="Année de la campagne"
            type="number"
            [value]="campaignYear()"
            (valueChange)="onYearChange($event ? $event.toString() : '')">
          </oui-textfield>
        </div>

        <div class="form-actions">
          <oui-button
            iconSuffix="rule"
            [disabled]="!canSubmit()"
            (click)="validate()">
            @if (validating()) {
              Validation en cours…
            } @else {
              Contrôler le fichier
            }
          </oui-button>
        </div>

        @if (awaitingConfirmation() && validationSummary(); as summary) {
          <dbw-state-card
            [appearance]="summary.errorCount > 0 ? 'error' : 'info'"
            title="Résumé des actions">
            <ul class="validation-summary">
              <li>{{ summary.emailDocumentCount }} bordereau(x) à envoyer par <strong>mail</strong></li>
              <li>{{ summary.courrierDocumentCount }} bordereau(x) à envoyer par <strong>courrier</strong></li>
              <li>{{ summary.contractLineCount }} ligne(s) de contrat pour {{ summary.consultantCount }} consultant(s)</li>
              <li>{{ summary.plannedBatchCount }} lot(s) d'envoi de mails prévu(s) (9500 max par lot)</li>
              <li>{{ summary.errorCount }} erreur(s), {{ summary.warningCount }} avertissement(s)</li>
            </ul>

            @if (summary.issues.length > 0) {
              <div class="issues-list">
                @for (issue of summary.issues.slice(0, 50); track $index) {
                  <p class="issue-item">
                    <oui-tag
                      type="status"
                      [status]="issue.severity === 'Error' ? 'error' : 'pending'">
                      {{ issue.severity === 'Error' ? 'Erreur' : 'Avertissement' }}
                    </oui-tag>
                    <span>
                      @if (issue.sheet) {
                        [{{ issue.sheet }}@if (issue.rowNumber > 0) { — ligne {{ issue.rowNumber }}}]
                      }
                      {{ issue.message }}
                    </span>
                  </p>
                }
                @if (summary.issues.length > 50) {
                  <p class="issue-item">… et {{ summary.issues.length - 50 }} autre(s).</p>
                }
              </div>
            }

            <div class="form-actions">
              <oui-button
                iconSuffix="upload_file"
                [disabled]="importing()"
                (click)="confirmImport()">
                @if (importing()) {
                  Import en cours…
                } @else {
                  Confirmer l'import
                }
              </oui-button>
              <oui-button
                type="text"
                (click)="cancelValidation()">
                Annuler
              </oui-button>
            </div>
          </dbw-state-card>
        }
      </article>
    } @else {
      <dbw-state-card
        appearance="info"
        title="Import non autorisé"
        [compact]="true">
        <p>Vous n'êtes pas autorisé à importer une campagne coûts ex-post.</p>
      </dbw-state-card>
    }

    @if (loading() && campaigns().length === 0) {
      <dbw-state-card
        title="Chargement"
        appearance="loading">
        <p>Lecture des campagnes coûts ex-post.</p>
      </dbw-state-card>
    } @else if (error()) {
      <dbw-state-card
        title="Erreur"
        appearance="error">
        <p>{{ error() }}</p>
      </dbw-state-card>
    } @else {
      <section class="dbw-card dbw-card--xl table-card">
        <div class="table-card__header">
          <div>
            <h5>Campagnes</h5>
            <p>{{ campaigns().length }} campagne(s) enregistrée(s).</p>
          </div>
        </div>

        @if (campaigns().length === 0) {
          <div class="empty-state">Aucune campagne coûts ex-post n'a encore été importée.</div>
        } @else {
          <div class="table-shell">
            <oui-table
              class="data-table"
              [lineSizing]="'dense'">
              <table
                mat-table
                [dataSource]="campaigns()">
                <ng-container matColumnDef="year">
                  <th mat-header-cell *matHeaderCellDef>Année</th>
                  <td mat-cell *matCellDef="let row">
                    <a [routerLink]="appRoutePaths.controleGestion.coutsExPostCampaign(row.id)">
                      <strong>{{ row.year }}</strong>
                    </a>
                  </td>
                </ng-container>

                <ng-container matColumnDef="fileName">
                  <th mat-header-cell *matHeaderCellDef>Fichier</th>
                  <td mat-cell *matCellDef="let row">{{ row.fileName }}</td>
                </ng-container>

                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef>Statut</th>
                  <td mat-cell *matCellDef="let row">
                    <oui-tag
                      type="status"
                      [status]="row.statusLevel">
                      {{ getStatusLabel(row) }}
                    </oui-tag>
                  </td>
                </ng-container>

                <ng-container matColumnDef="createdBy">
                  <th mat-header-cell *matHeaderCellDef>Importée par</th>
                  <td mat-cell *matCellDef="let row">{{ row.createdBy }}</td>
                </ng-container>

                <ng-container matColumnDef="createdAt">
                  <th mat-header-cell *matHeaderCellDef>Importée le</th>
                  <td mat-cell *matCellDef="let row">{{ formatDate(row.createdAtUtc) }}</td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef>Actions</th>
                  <td mat-cell *matCellDef="let row">
                    <div class="row-actions">
                      <oui-button
                        type="text"
                        iconSuffix="visibility"
                        [routerLink]="appRoutePaths.controleGestion.coutsExPostCampaign(row.id)">
                        Piloter
                      </oui-button>
                    </div>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
              </table>
            </oui-table>
          </div>
        }
      </section>
    }
  </section>
</section>
